import { useEffect, useId, useState } from "react";
import { encodeVerifyQr, normalizeSafetyDigits } from "@veto/domain";
import { IconDetails } from "@veto/ui";
import { QrScannerModal } from "./QrScannerModal";

/**
 * FR-CON-002 verification section: one-tap out-of-band attestation plus
 * scan/show QR flows. The security comes from the out-of-band comparison —
 * typing digits is optional (copy-paste bypasses it); attestation does not.
 */
export function ContactVerify({
  contact,
  onVerify,
}: {
  contact: {
    displayName: string;
    safetyNumber: string;
    verificationState: "unverified" | "verified" | "changed";
  };
  onVerify: () => void | Promise<void>;
}) {
  const tipId = useId();
  const [tipOpen, setTipOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [compared, setCompared] = useState(false);
  const [legacyDigits, setLegacyDigits] = useState("");
  const [verifyErr, setVerifyErr] = useState<string | null>(null);

  useEffect(() => {
    if (!showQr) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void import("qrcode")
      .then((m) =>
        m.default.toDataURL(encodeVerifyQr(contact.safetyNumber), {
          width: 200,
          margin: 2,
          color: { dark: "#05070C", light: "#FFFFFF" },
          errorCorrectionLevel: "M",
        }),
      )
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showQr, contact.safetyNumber]);

  const expected = normalizeSafetyDigits(contact.safetyNumber);

  return (
    <div className="field verify-section">
      <div className="verify-label-row details-section-label">
        <span>Verify this contact</span>
        <button
          type="button"
          className="icon-btn info-tip-btn"
          aria-label="Why verifying matters"
          aria-expanded={tipOpen}
          aria-controls={tipId}
          title="Why verifying matters"
          onClick={() => setTipOpen((v) => !v)}
        >
          <IconDetails size={15} />
        </button>
      </div>

      {tipOpen && (
        <p className="verify-why muted text-sm" id={tipId} role="note">
          Messages are always end-to-end encrypted — but the first key exchange travels over the
          internet. Until you verify, an attacker who intercepted your invite exchange (a hostile
          relay or network) could silently read this chat. Verifying compares a fingerprint only
          your two devices can produce, and takes about a minute. For maximum security, verify
          everyone you talk to sensitive matters about. You can keep messaging meanwhile; if their
          keys ever change, sending pauses automatically.
        </p>
      )}

      {contact.verificationState === "changed" && (
        <p className="verify-why muted text-sm" role="note">
          This contact&apos;s identity keys changed. Verifying confirms the new keys are really
          theirs — until then, sending stays paused for safety.
        </p>
      )}

      <div className="verify-methods">
        <button
          className="btn secondary small"
          type="button"
          onClick={() => {
            setScanOpen(true);
            setVerifyErr(null);
          }}
        >
          Scan their code
        </button>
        <button
          className="btn secondary small"
          type="button"
          onClick={() => {
            setShowQr((v) => !v);
            setVerifyErr(null);
          }}
          aria-expanded={showQr}
        >
          {showQr ? "Hide my code" : "Show my code"}
        </button>
      </div>

      {showQr && (
        <div className="verify-qr-box">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt={`Verify QR code for ${contact.displayName}`}
              width={200}
              height={200}
            />
          ) : (
            <div className="muted small" role="status">
              Generating code…
            </div>
          )}
          <ol className="verify-steps muted text-sm">
            <li>Meet in person (or hold devices side by side).</li>
            <li>
              They open this chat → <strong>Details → Scan their code</strong>.
            </li>
            <li>They point their camera at this screen — a match verifies them.</li>
            <li>Swap roles so both of you are verified.</li>
          </ol>
          <p className="muted text-xs" style={{ margin: 0 }}>
            Show it on a screen — don&apos;t send this code through another app. A listener on that
            channel could swap it and defeat the check.
          </p>
        </div>
      )}

      {scanOpen && (
        <QrScannerModal
          displayName={contact.displayName}
          expectedDigits={contact.safetyNumber}
          onVerified={() => void onVerify()}
          onClose={() => setScanOpen(false)}
        />
      )}

      <details className="verify-compare" open={!showQr && !scanOpen}>
        <summary>Or compare the numbers out of band</summary>
        <label className="check" htmlFor="comparedSafety">
          <input
            id="comparedSafety"
            type="checkbox"
            checked={compared}
            onChange={(e) => {
              setCompared(e.target.checked);
              setVerifyErr(null);
            }}
          />
          <span>
            I compared this safety number with {contact.displayName} on a separate channel (in
            person, call, or scanned QR).
          </span>
        </label>
        {/* Legacy e2e hook: #confirmSafety stays for automated fills, but typing is optional. */}
        <input
          id="confirmSafety"
          value={legacyDigits}
          onChange={(e) => {
            setLegacyDigits(e.target.value);
            setVerifyErr(null);
          }}
          placeholder="Optional: paste digits to double-check"
          autoComplete="off"
          inputMode="numeric"
          aria-hidden={false}
          style={{ marginTop: 8 }}
        />
        {verifyErr && (
          <p className="muted text-sm" role="alert" style={{ color: "var(--danger, #c44)", margin: 0 }}>
            {verifyErr}
          </p>
        )}
        <button
          className="btn"
          type="button"
          disabled={!compared}
          title={compared ? "Mark verified" : "Confirm you compared first"}
          onClick={() => {
            // If legacy field was filled (old scripts), validate it; empty = attestation only.
            if (legacyDigits.trim()) {
              const got = normalizeSafetyDigits(legacyDigits);
              if (!got || got !== expected) {
                setVerifyErr("Safety number does not match — verify in person before trusting");
                return;
              }
            }
            if (!compared) {
              setVerifyErr("Confirm you compared the safety number first.");
              return;
            }
            setVerifyErr(null);
            void onVerify();
            setLegacyDigits("");
          }}
        >
          Mark verified
        </button>
        <p className="muted text-sm" style={{ margin: 0 }}>
          Read the digits above aloud in person or on a call (not in this chat), then tick and tap.
          No typing needed — comparing + tapping is the security. Key changes still raise a visible
          alert and pause sending until re-verified.
        </p>
      </details>
    </div>
  );
}
